import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import OvertimeTodoList from '../components/OvertimeTodoList';
import useLiveRefresh from '../utils/useLiveRefresh';
import { getCurrentUserRole } from '../utils/authHelper';
import { formatDate } from '../utils/dateConverter';
import {
    approveOvertimeApproval,
    getPendingOvertimeApprovals,
    rejectOvertimeApproval,
} from '../services/OvertimeApprovalService';
import { useConfirm, usePrompt, useToast } from '../context/UIContext';
import { extractApiError } from '../utils/apiError';
import '../styles/KPI.css';

// Each current period sits next to the completed one before it, so "how much did
// I book last week / last month" is answered without doing the maths in your head.
const PERIODS = [
    { key: 'today',      label: 'Heute',         accent: '#3B82F6' },
    { key: 'this_week',  label: 'Diese Woche',   accent: '#2563EB', note: 'laufend' },
    { key: 'last_week',  label: 'Letzte Woche',  accent: '#60A5FA', range: 'last_week'  },
    { key: 'this_month', label: 'Dieser Monat',  accent: '#1E40AF', note: 'laufend' },
    { key: 'last_month', label: 'Letzter Monat', accent: '#93C5FD', range: 'last_month' },
    { key: 'this_year',  label: 'Dieses Jahr',   accent: '#1E293B' },
];

/** "27.07.–02.08." for a week, "Juli 2026" for a whole calendar month. */
function fmtRange(from, to) {
    if (!from || !to) return '';
    const start = new Date(from);
    const end = new Date(to);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
    const firstOfMonth = start.getDate() === 1;
    const lastOfMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate() === end.getDate();
    if (firstOfMonth && lastOfMonth && start.getMonth() === end.getMonth()) {
        return start.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    }
    const d = (v) => v.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
    return `${d(start)}–${d(end)}`;
}

function fmtDisplay(timeStr) {
    if (!timeStr || timeStr === '00:00') return '–';
    const [h, m] = timeStr.split(':').map(Number);
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
}

function toMins(timeStr) {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
}

export default function KPI() {
    const [stats, setStats]     = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    const navigate = useNavigate();
    const toast = useToast();
    const confirm = useConfirm();
    const prompt = usePrompt();
    const isAdmin = ['admin', 'super_admin'].includes(getCurrentUserRole());
    const [todos, setTodos] = useState({ items: [], total: 0 });
    const [todosLoading, setTodosLoading] = useState(isAdmin);
    const [todosError, setTodosError] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const loadTodos = useCallback(async () => {
        if (!isAdmin) return;
        try {
            setTodos(await getPendingOvertimeApprovals());
            setTodosError(null);
        } catch {
            setTodosError('Offene Anträge konnten nicht geladen werden.');
        } finally {
            setTodosLoading(false);
        }
    }, [isAdmin]);

    // Same cadence as the nav badge: an approval granted on another screen, or a day closed
    // by an employee, should not need a page reload to show up here.
    useLiveRefresh(loadTodos, { enabled: isAdmin });

    const handleApprove = useCallback(async (item) => {
        const who = item.displayName || 'Mitarbeiter';
        if (!await confirm(
            `Überstunden von ${who} am ${formatDate(item.date)} genehmigen?`,
            { confirmLabel: 'Genehmigen' },
        )) return;
        setBusyId(item.id);
        try {
            await approveOvertimeApproval(item.id);
            toast('Überstunden genehmigt.', 'success');
            await loadTodos();
        } catch (err) {
            toast(extractApiError(err, 'Fehler beim Genehmigen.'), 'error');
        } finally {
            // Only when it is still this row's: two approvals in flight would otherwise let the
            // first one re-enable the row that is still waiting.
            setBusyId(cur => (cur === item.id ? null : cur));
        }
    }, [confirm, loadTodos, toast]);

    const handleReject = useCallback(async (item) => {
        const who = item.displayName || 'Mitarbeiter';
        // The reason is asked for in the same step as the rejection, like a lost offer: one
        // collected in a later, separate step is one nobody enters.
        const reason = await prompt(
            `Überstunden von ${who} am ${formatDate(item.date)} ablehnen?`,
            { confirmLabel: 'Ablehnen', danger: true, placeholder: 'Grund (optional)' },
        );
        if (reason === null) return;
        setBusyId(item.id);
        try {
            await rejectOvertimeApproval(item.id, reason || null);
            toast('Überstunden abgelehnt.', 'success');
            await loadTodos();
        } catch (err) {
            toast(extractApiError(err, 'Fehler beim Ablehnen.'), 'error');
        } finally {
            setBusyId(cur => (cur === item.id ? null : cur));
        }
    }, [loadTodos, prompt, toast]);

    useEffect(() => {
        const controller = new AbortController();
        api.get('/Stats/me', { signal: controller.signal })
            .then(res => { setStats(res.data); setLoading(false); })
            .catch(err => {
                if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
                    setError('Statistiken konnten nicht geladen werden.');
                }
            })
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, []);

    const dateStr = new Date().toLocaleDateString('de-DE', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });

    const projects  = stats?.projects_this_year ?? [];
    const maxMins   = projects.length > 0 ? toMins(projects[0]?.total_work_time) : 1;

    // Kept up while /Stats/me is still loading or has failed: they are two endpoints, and the
    // approvals used to disappear behind "Lade Statistiken…" for as long as the slower one took.
    const todoList = isAdmin && (
        <OvertimeTodoList
            items={todos.items}
            total={todos.total}
            loading={todosLoading}
            error={todosError}
            busyId={busyId}
            onApprove={handleApprove}
            onReject={handleReject}
            onOpenAll={() => navigate('/employee?tab=overtime')}
        />
    );

    return (
        <div className="kpi-container">

            <div className="kpi-hero">
                <div>
                    <h2>Mein Dashboard</h2>
                    <p className="kpi-hero-sub">
                        {isAdmin ? 'Offene Anträge und deine Arbeitszeiten' : 'Deine Arbeitszeiten auf einen Blick'}
                    </p>
                </div>
                <div className="kpi-hero-date">{dateStr}</div>
            </div>

            {/* Above the personal figures: a to-do list below the fold is one nobody works
                through. */}
            {todoList}

            {loading ? (
                <p className="empty-msg">Lade Statistiken…</p>
            ) : error ? (
                <p className="error-text">{error}</p>
            ) : (
              <>

            <div className="kpi-stat-grid">
                {PERIODS.map(({ key, label, accent, note, range }) => {
                    const sub = range
                        ? fmtRange(stats?.[`${range}_from`], stats?.[`${range}_to`])
                        : note;
                    return (
                        <div key={key} className="kpi-stat-card" style={{ borderTopColor: accent }}>
                            <div className="kpi-stat-label">{label}</div>
                            <div className="kpi-stat-value">{fmtDisplay(stats?.[key])}</div>
                            <div className="kpi-stat-raw">
                                {(!stats?.[key] || stats?.[key] === '00:00') ? '–' : stats?.[key]}
                                {sub && <span className="kpi-stat-period">{sub}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="kpi-projects-card">
                <div className="kpi-projects-header">
                    <h3>Projekte dieses Jahr</h3>
                    {projects.length > 0 && (
                        <span className="kpi-count-badge">{projects.length} Projekte</span>
                    )}
                </div>

                {projects.length === 0 ? (
                    <p className="kpi-empty">Keine Projektzeiten für dieses Jahr erfasst.</p>
                ) : (
                    projects.map(p => {
                        const pct = Math.round((toMins(p.total_work_time) / maxMins) * 100);
                        return (
                            <div key={p.project_id} className="kpi-project-row">
                                <div>
                                    <div className="kpi-project-name"  title={p.project_name}>{p.project_name}</div>
                                    <div className="kpi-project-company" title={p.company_name}>{p.company_name}</div>
                                </div>
                                <div className="kpi-bar-track">
                                    <div className="kpi-bar-fill" style={{ width: `${pct}%` }} />
                                </div>
                                <div className="kpi-project-time">
                                    <span className="kpi-project-time-main">{fmtDisplay(p.total_work_time)}</span>
                                    <span className="kpi-project-time-raw">{p.total_work_time}</span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
              </>
            )}

        </div>
    );
}
